import type { ReactNode } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { ForgePageLoader } from "@/components/ForgeLoader";
import { AccessLoadError } from "@/components/layout/AccessLoadError";
import { useAccessContext } from "@/hooks/useAccessContext";
import { useOrganization } from "@/hooks/useOrganization";
import { STAFF_APP_ROLES } from "@/hooks/accessRoleUtils";
import { taskLinkIntent } from "@/lib/task-notification-link";

function TaskLinkNotice({ children }: { children: ReactNode }) {
  return <AppLayout title="Tasks" showSidebar={false}><p role="status">{children}</p></AppLayout>;
}

/** Runs inside ProtectedRoute, before the existing staff gate can discard task intent. */
export function TaskNotificationGate() {
  const location = useLocation();
  const { taskId: recordId } = useParams();
  const intent = taskLinkIntent(location.search, recordId);
  // Only a task link needs freshly verified access to choose its destination. A plain Tasks visit
  // keeps the staff gate's default access (last good roles, no polling), and an organization left
  // in the URL after the task closed no longer applies to it.
  const access = useAccessContext({ refreshAccess: intent.hasTarget });
  const org = useOrganization();
  if (!intent.hasTarget) return <Outlet />;
  if (access.isLoading || (access.isFetching && !access.accessRevision) || org.isLoading) return <ForgePageLoader />;
  if (access.rolesUnavailable) return <AccessLoadError onRetry={() => { void access.refetch(); }} />;
  if (!intent.taskId || intent.invalidOrganization) {
    return <TaskLinkNotice>This task is unavailable.</TaskLinkNotice>;
  }
  if (intent.organizationId && intent.organizationId !== org.organizationId?.toLowerCase()) {
    return <TaskLinkNotice>Choose the organization for this notification to view its task.</TaskLinkNotice>;
  }
  // Choose the destination using active-org grants. The existing staff gate still guards its children.
  const staff = access.data?.capabilities.isOrgAdmin || access.data?.org_app_roles.some(role => STAFF_APP_ROLES.includes(role));
  if (!staff) {
    const params = new URLSearchParams(location.search);
    params.delete("id"); params.set("taskId", intent.taskId);
    if (!intent.organizationId && org.organizationId) params.set("organizationId", org.organizationId);
    return <Navigate to={`/volunteer/tasks?${params}${location.hash}`} replace />;
  }
  if (!recordId && new URLSearchParams(location.search).has("id")) {
    const params = new URLSearchParams(location.search);
    params.delete("id"); params.set("taskId", intent.taskId);
    if (!intent.organizationId && org.organizationId) params.set("organizationId", org.organizationId);
    return <Navigate to={`/tasks?${params}${location.hash}`} replace />;
  }
  return <Outlet />;
}
